import { useEffect, useRef, useState } from 'react';

function readPrefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Raqamni joriy ko'rsatilgan qiymatdan `target` ga `requestAnimationFrame`
 * yordamida silliq sanab chiqadi. Analitika KPI / daromad raqamlarini
 * "jonlantirish" uchun.
 *
 * - `prefers-reduced-motion: reduce` yoqilgan bo'lsa animatsiya o'tkazilmaydi —
 *   darhol `target` qaytariladi (a11y). Tekshiruv komponent hayoti davomida
 *   bir marta o'qiladi (`useRef`), har render'da DOM so'rovi qilinmaydi.
 * - `target` o'zgarsa (masalan period almashganda), animatsiya HOZIR ko'rinib
 *   turgan qiymatdan yangi `target` ga silliq davom etadi (stale qiymatdan emas).
 * - Komponent unmount bo'lsa rAF bekor qilinadi.
 */
export function useCountUp(
  target: number,
  options?: { durationMs?: number; enabled?: boolean },
): number {
  const durationMs = options?.durationMs ?? 1200;
  const enabled = options?.enabled ?? true;

  const prefersReducedMotionRef = useRef<boolean>(readPrefersReducedMotion());
  const prefersReducedMotion = prefersReducedMotionRef.current;

  const [value, setValue] = useState<number>(
    enabled && !prefersReducedMotion ? 0 : target,
  );

  const frameRef = useRef<number | null>(null);
  // Joriy ko'rinib turgan qiymat — har frame'da yangilanadi, target o'zgarganda
  // animatsiya aynan shu qiymatdan boshlanadi (stale closure muammosi yo'q).
  const currentValueRef = useRef<number>(value);

  useEffect(() => {
    if (!enabled || prefersReducedMotion || durationMs <= 0) {
      currentValueRef.current = target;
      setValue(target);
      return;
    }

    const startValue = currentValueRef.current;
    let startTime: number | null = null;

    const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

    const tick = (now: number): void => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = easeOutCubic(progress);
      const next =
        progress >= 1 ? target : startValue + (target - startValue) * eased;
      currentValueRef.current = next;
      setValue(next);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs, enabled, prefersReducedMotion]);

  return value;
}
